<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from '../../../services/http';
	//Stores
	import { user_data, user_settings } from 'src/stores/account/user';
import { app } from 'src/stores/session/native-bridge';
	//Design system components
	import {
		Button,
		Input,
		Row,
		Col,
		toast,
		Toggle,
		type ToastElement,
		Alert,
	} from '@Shelly/shelly-design-system';
	import { BiometryService } from 'src/services/biometry/biometry-service';
	import { get } from 'svelte/store';

	let pinValue = $user_data.lock_pin;
	let error = '';
	let biometricToggleValue = BiometryService.isBiometryTurnedOnByUser();
	let isBiometryAvailable = BiometryService.isBiometryAvailable();
	let biometricModalTranslations = {
		title: $format('biometrics_turn_on_title', { default: 'Biometric Auth' }),
		subtle: '',
		description: $format('biometrics_turn_on_desc', {
			default: 'Turn on biometric authentication when pin is required.',
		}),
		cancel: $format('button_cancel', { default: 'Cancel' }),
	};

	$: if (typeof pinValue === 'object' || pinValue?.length < 4 || pinValue?.length > 4) {
		error = $format('user_pin_lock_limit_err');
	} else {
		error = '';
	}

	export const handleUserPin = async (pin) => {
		const res = await postReq('/user/set_user_pin', {
			pin: pin,
		});

		user_data.update({ lock_pin: pin });

		if (res.isok) {
			toast.open({
				type: 'success',
				title: $format('user_pin_lock_scc_toast'),
			});
		} else {
			toast.open({ type: 'danger', title: $format('user_pin_lock_err_toast') });
		}
	};

	const handle_input = (e) => {
		if (isNaN(Number(e.data))) {
			//this must remove all letters from value, need design system solution
			pinValue = pinValue
				.toString()
				.replace(/[^0-9]/g, '')
				.replace(/(\..*?)\..*/g, '$1');

			if (typeof pinValue === 'number' && pinValue === 0) {
				pinValue = '0';
			}
		}
	};

	const toggleBiometrics = async () => {
		const successToast = {
			type: 'success',
			title: $format('biometric_auth_toggle_success_toast', {
				default: 'Biometric authentication setting updated successfully.',
			}),
		} as ToastElement;
		const errorToast = {
			type: 'danger',
			title: $format('biometric_auth_toggle_error_toast', {
				default: 'Biometric authentication failed',
			}),
			duration: 4000,
		} as ToastElement;

		if (!BiometryService.isBiometryAvailable()) {
			toast.open(errorToast);
			setTimeout(() => {
				biometricToggleValue = !biometricToggleValue;
			}, 100);
			return;
		}

		const res = await BiometryService.startBiometricAuthentication(
			biometricModalTranslations.title,
			biometricModalTranslations.subtle,
			biometricModalTranslations.description,
			biometricModalTranslations.cancel,
			'',
		);

		if (res?.error === 'Canceled' || res.error === 'userCancel') {
			return;
		}

		if (!res.success) {
			toast.open(errorToast);
			biometricToggleValue = !biometricToggleValue;
			return;
		}

		const updateSettingResponse = await BiometryService.toggleBiometrySetting(biometricToggleValue);

		if (updateSettingResponse.isok) {
			const userSettings = get(user_settings);

			userSettings.biometrics.enabled = biometricToggleValue ? '1' : '0';
			user_settings.set(userSettings);
			toast.open(successToast);
		}
	};
</script>

<Row>
	<Col xs="12" md="3">
		<Input
			disabled={$user_data?.installer}
			{error}
			icon={'fas fa-key'}
			bind:value={pinValue}
			label={typeof $user_data.lock_pin === 'number' && $user_data.lock_pin !== 0
				? $user_data.lock_pin
				: $format('placeholder_set_pin')}
			type={'password'}
			on:input={handle_input}
			on:keydown={handle_input}
			id="input-mbe3tk5"
			aria-labelledby="input-mbe3tk5"
		/>
	</Col>
	<Col xs="12" md="3">
		<Button
			size="m"
			class="width-md-auto width-sm-full"
			disabled={error}
			on:click={() => handleUserPin(pinValue)}>{$format('button_save')}</Button
		>
	</Col>
</Row>
{#if $app}
	{#if !isBiometryAvailable}
		<Row>
			<Col>
				<Alert
					type="warning"
					heading={$format('biometry_warning_not_avaliable', {
						default:
							'Biometric authentication is not available on this device. Check your device settings to enroll.',
					})}
					class="mt-s"
				/>
			</Col>
		</Row>
	{/if}
	<Row class="mt-s">
		<Col xs="12" md="3">
			<Toggle
				bind:checked={biometricToggleValue}
				disabled={!isBiometryAvailable}
				size="m"
				on:click={toggleBiometrics}
				id="toggle-fjalfgi"
				aria-labelledby="toggle-fjalfgi"
			>
				{$format('enable_biometric_auth', {
					default: 'Enable Biometric Authentication',
				})}
			</Toggle>
		</Col>
	</Row>
{/if}
